import {
  CloudflareTurnTokenResponse,
  createCloudflareAuthenticatedTurnTokenCreds,
  createCloudflarePublicTurnTokenCreds
} from '@goldcast/api/backend';
import { StreamTypeEnum as StreamTypeEnumApi } from '@goldcast/api/content';

import { backendHaFetcher } from './backendHaFetcher';
import { getAgoraToken } from './event.service';
import loggerService from './logger.service';
import { retry } from './utils';
import { getUserSuffixForAgoraToken, type AgoraToken } from './utils/liveMedia.utils';

import type { IAgoraRTCClient } from './agora.types';

import { AGORA, getEnv } from '@/constants';
import store from '@/store';
import { exponentialRetry } from '@/utils/utils';

export async function fetchTurnServerCredentials(isTechCheck: boolean): Promise<CloudflareTurnTokenResponse> {
  return isTechCheck ? createCloudflarePublicTurnTokenCreds({}) : createCloudflareAuthenticatedTurnTokenCreds({});
}

export async function fetchAgoraToken(channelName: string, isScreen: Boolean | undefined, isAttendee: Boolean) {
  const userSuffix = getUserSuffixForAgoraToken(isScreen);
  const currentUserId = store.state.user.currentUser?.id;
  const channelSuffix = channelName.split('|')[1] ? '|' + channelName.split('|')[1] : '';
  const response = await getAgoraToken(channelName, userSuffix, currentUserId, isAttendee);

  return {
    token: (response.data as any).token || (response.data as any).key,
    suffix: userSuffix,
    channel_suffix: channelSuffix
  };
}

async function fetchAttendeeAgoraTokenForChannel(channelName: string) {
  try {
    const channelId = channelName.split('|')[0];
    const agoraTokenFromStore = store.state.mediaroom.agoraTokens.find(el => el.room == channelId);

    if (agoraTokenFromStore) {
      return agoraTokenFromStore;
    }

    loggerService.track('[API_CONSOLIDATION] agora token cache miss', {
      tokensInStore: store.state.mediaroom.agoraTokens.length,
      channelName
    });
    const tokens = await store.dispatch('mediaroom/fetchBulkAgoraToken');
    const token = tokens?.find((el: { room: string }) => el.room === channelId);
    if (token) {
      return token;
    }

    loggerService.track('[API_CONSOLIDATION] agora bulk fetch miss - falling back to individual token fetch', {
      channelName
    });
    return fetchAgoraToken(channelName, false, true);
  } catch (error) {
    loggerService.error('agora token dispatch error, falling back to individual fetch', error);
    return fetchAgoraToken(channelName, false, true);
  }
}

export async function getAgoraTokenToJoinClient(
  channelName: string,
  isAttendee: boolean,
  isScreen: Boolean | undefined,
  forceFetchFromApi: Boolean = false
) {
  const useConsolidation = store.state.event.useEventApiConsolidation;
  if (isAttendee && !forceFetchFromApi) {
    loggerService.log('[AgoraToken] bulk-unprivileged path', {
      flow: useConsolidation && !isAttendee ? 'consolidation-flag' : 'original',
      channelName,
      isAttendee,
      useConsolidation
    });
    return fetchAttendeeAgoraTokenForChannel(channelName);
  }
  return fetchAgoraToken(channelName, isScreen, isAttendee);
}

export async function getAgoraTokenWithRetries(
  channelName: string,
  isAttendee: boolean,
  isScreen: Boolean | undefined,
  forceFetchFromApi: Boolean = false
): Promise<AgoraToken> {
  const response: AgoraToken = await retry(
    () => {
      if (store.state.mediaroom.mediaRooms?.[channelName]?.desiredStatus !== 'CONNECTED') {
        return Promise.reject('Fail to fetch Agora Token');
      }

      return getAgoraTokenToJoinClient(channelName, isAttendee, isScreen, forceFetchFromApi);
    },
    5,
    1000
  );
  return new Promise(resolve => {
    resolve(response);
  });
}

const sharedAgoraIntegerIds: number[] = [];

export async function syncUserUIDsWithBackend(client: IAgoraRTCClient & any) {
  try {
    const backendHaUrl = getEnv('VUE_APP_BACKEND_HA_URL');
    if (!backendHaUrl) {
      loggerService.error('VUE_APP_BACKEND_HA_URL is not configured');
      return;
    }
    const coreUserId = store.state.user.currentUser?.id;
    const integerAgoraId = client?._joinInfo?.uid;
    const stringAgoraId = client?._joinInfo?.stringUid;
    const channel = client?._joinInfo?.cname;

    const isScreen = stringAgoraId?.includes(AGORA.UID_SUFFIX.SCREEN_STREAM);
    if (!sharedAgoraIntegerIds.includes(integerAgoraId)) {
      sharedAgoraIntegerIds.push(integerAgoraId);

      const userData = [
        {
          agora_uid: integerAgoraId,
          broadcast_id: channel,
          core_user: {
            id: coreUserId || ''
          },
          core_user_id: String(coreUserId || ''),
          stream_type: isScreen ? ('SCREENSHARE' as StreamTypeEnumApi) : ('SPEAKER' as StreamTypeEnumApi)
        }
      ];

      exponentialRetry(
        async () => {
          try {
            return await backendHaFetcher({
              url: '/user/agora/bulk_create/',
              method: 'POST',
              body: userData,
              queryParams: {
                broadcast: channel
              }
            });
          } catch (error: any) {
            const status = error?.status || error?.response?.status || error?.statusCode;
            if (status && status >= 400 && status < 500) {
              // 4xx errors are client-side and won't resolve on retry
              loggerService.error('Non-retryable error syncing uid data', error);
              return;
            }
            loggerService.error('Retryable error while syncing uid data', error);
            // Network errors (TypeError: Failed to fetch), timeouts (AbortError),
            // and 5xx errors are transient — re-throw so exponentialRetry can retry
            throw error;
          }
        },
        {
          retries: 5,
          delayMs: 5000,
          onRetry: (error, attempt) =>
            loggerService.warn(`Bulk create user data to video editor service retry attempt ${attempt}/5`, error),
          onMaxReached: error =>
            loggerService.error('Bulk create user data to video editor service failed after 5 retries', error)
        }
      );
    }
  } catch (error) {
    loggerService.error('Error syncing uid data to video editor service', error);
  }
}
